import { useEffect } from "react";
import authStore from "../../stores/auth/authStore";
import contentStore from "../../stores/cms/contentStore";
import complianceDocumentStore from "../../stores/compliance-documents/complianceDocumentStore";
import deliveryStore from "../../stores/delivery/deliveryStore";
import deliveryDatesStore from "../../stores/deliveryDates/deliveryDatesStore";
import deliveryWindowStore from "../../stores/deliveryDates/deliveryWindowStore";
import featuresStore from "../../stores/features/featuresStore";
import productStore from "../../stores/product/productStore";
import serverEventStore from "../../stores/serverEventsStore";
import uiStore from "../../stores/uiStore";
import theme from "../../themes/theme";
import { reportError } from "../errorReporting";
import { ensureError, TokenExpiryError } from "../errors";
import { logger } from "../logger";

const shouldUseBrowserMocks = (): boolean => {
   if (!__ENABLE_BROWSER_MOCKS__ || theme.storeId !== "tinehandel") return false;
   return new URLSearchParams(window.location.search).has("scenario");
};

const prepareEnvironment = async (): Promise<void> => {
   await uiStore.ensureDataVersionAvailable();
   await featuresStore.fetchEnabledConfigCatFeatures();
};

export const loadStaticData = (location: string): void => {
   // Static data is loaded in parallel with session verification, so the login state (and login button)
   // resolves without waiting for the CDN. Login does not need Contentful, so it is loaded separately.
   void contentStore.fillStore(location).catch((err) => {
      reportError(ensureError(err), { context: "Contentful loading during startup" });
   });

   // authStore.verifySession awaits productsReady before initializing the customer
   void productStore.loadProducts();

   void deliveryDatesStore.fetchHolidays();
};

const reportSessionStartupError = (err: unknown): void => {
   if (err instanceof TokenExpiryError) return;

   const wrappedError: Error = ensureError(err);
   reportError(wrappedError, { context: "Session Check during startup" });
   logger.log("auth", "Not already logged in, reason: ", wrappedError);
};

const restoreSession = async (redirectTo: (to: string) => void): Promise<void> => {
   try {
      const isImpersonating = await authStore.getSessionFromQuery();
      if (isImpersonating) {
         redirectTo("/");
      }
      if (featuresStore.liveDeliveryStatusEnabled) {
         deliveryStore.init();
      }

      deliveryWindowStore.init();
      await authStore.verifySession(null);
   } catch (err) {
      reportSessionStartupError(err);
   }
};

export const fetchData = async (location: string, redirectTo: (to: string) => void): Promise<void> => {
   loadStaticData(location);
   await restoreSession(redirectTo);
};

export const useAppStartup = (location: string, redirectTo: (to: string) => void): void => {
   useEffect(() => {
      let disposeComplianceDocuments: (() => void) | undefined;
      let isDisposed = false;
      const start = (): void => {
         if (isDisposed) return;

         disposeComplianceDocuments = complianceDocumentStore.init();
         void fetchData(location, redirectTo);
      };

      if (shouldUseBrowserMocks()) {
         void import("../../mocks/initializeMocks")
            .then((mod) => mod.default())
            .then(prepareEnvironment)
            .then(() => {
               start();
               serverEventStore.init();
            });
      } else {
         void prepareEnvironment().then(start);
         serverEventStore.init();
      }

      window.addEventListener("keydown", uiStore.handleShortcutKeypress);
      return () => {
         isDisposed = true;
         disposeComplianceDocuments?.();
         serverEventStore.disconnect();
         window.removeEventListener("keydown", uiStore.handleShortcutKeypress);
      };
   }, []);
};
